import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useVault } from "@/context/VaultContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SetupWizard } from "@/components/passord/SetupWizard";
import { UnlockScreen } from "@/components/passord/UnlockScreen";
import { EntrySheet, CATEGORIES } from "@/components/passord/EntrySheet";
import { EntryRow } from "@/components/passord/EntryRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { KeyRound, Plus, Search, Star, X, ShieldCheck, HeartPulse } from "lucide-react";
import { Link } from "react-router-dom";
import { analyzeVault } from "@/lib/passwordHealth";

export default function VaultPage() {
  const { status, error, entries, settings, saveEntry, refreshMeta, breach } = useVault();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("alle");
  const [sheet, setSheet] = useState({ open: false, entry: null });
  // Veiviseren må bli stående til brukeren har bekreftet gjenopprettingskoden – selv om status blir «unlocked» underveis.
  const [wizardActive, setWizardActive] = useState(false);

  useEffect(() => { if (status === "none") setWizardActive(true); else if (status === "locked" || status === "error") setWizardActive(false); }, [status]);
  useEffect(() => { setQ(params.get("q") || ""); }, [params]);
  useEffect(() => {
    if (params.get("ny") && status === "unlocked") {
      setSheet({ open: true, entry: null });
      const p = new URLSearchParams(params); p.delete("ny"); setParams(p, { replace: true });
    }
  }, [params, status, setParams]);

  const usedCats = useMemo(() => Array.from(new Set(entries.map((e) => e.category || "Annet"))).sort((a, b) => CATEGORIES.indexOf(a) - CATEGORIES.indexOf(b)), [entries]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return entries
      .filter((e) => (cat === "alle" ? true : cat === "favoritter" ? e.favorite : (e.category || "Annet") === cat))
      .filter((e) => !s || [e.title, e.username, e.url, e.notes, e.category].some((v) => (v || "").toLowerCase().includes(s)))
      .sort((a, b) => (b.favorite === true) - (a.favorite === true) || (a.title || "").localeCompare(b.title || "", "nb"));
  }, [entries, q, cat]);

  const toggleFavorite = async (entry) => {
    try { await saveEntry({ ...entry, favorite: !entry.favorite }, entry.id, []); } catch (e) { toast.error(errorMessage(e)); }
  };

  if (status === "loading") return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-72" /></div>;
  if (status === "error") return <EmptyState title="Kunne ikke laste hvelvet" description={error} actionLabel="Prøv igjen" onAction={refreshMeta} />;
  if (status === "none" || (wizardActive && status === "unlocked")) return (
    <div className="space-y-6">
      <PageHeader eyebrow="Passord" title="Passordhvelv" description="Samle alle pålogginger på ett trygt sted." />
      <SetupWizard onDone={() => setWizardActive(false)} />
    </div>
  );
  if (status === "locked") return (
    <div className="space-y-6">
      <PageHeader eyebrow="Passord" title="Passordhvelv" description="Kryptert med hovedpassordet ditt – kun du kan låse opp." />
      <UnlockScreen />
    </div>
  );

  const favorites = entries.filter((e) => e.favorite).length;
  const health = entries.length ? analyzeVault(entries, breach.results) : null;
  const healthTone = health ? ({ success: "text-success", primary: "text-primary", warn: "text-warn", flag: "text-flag" }[health.tone]) : "";

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Passord"
        title="Mine passord"
        description={entries.length ? `${entries.length} ${entries.length === 1 ? "oppføring" : "oppføringer"} · dekryptert lokalt i nettleseren.` : "Hvelvet er tomt. Legg inn din første pålogging."}
        actions={(
          <div className="flex items-center gap-2">
            {health ? (
              <Button variant="outline" asChild>
                <Link to="/passord/helse" data-testid="vault-health-chip" aria-label={`Passordhelse ${health.score} av 100, ${health.flagged.length} funn`}>
                  <HeartPulse className={cn("h-4 w-4 mr-2", healthTone)} /> Helse <span className={cn("ml-1.5 font-semibold tnum", healthTone)}>{health.score}</span>
                  {health.flagged.length ? <span className={cn("ml-2 rounded-full px-1.5 py-0.5 text-[11px] tnum", health.counts.leaked ? "status-avvist border" : "bg-secondary text-muted-foreground")} data-testid="vault-health-findings">{health.counts.leaked ? `${health.counts.leaked} lekket` : `${health.flagged.length} funn`}</span> : null}
                </Link>
              </Button>
            ) : null}
            <Button onClick={() => setSheet({ open: true, entry: null })} data-testid="vault-new-entry-button"><Plus className="h-4 w-4 mr-2" /> Nytt passord</Button>
          </div>
        )}
      />

      {entries.length === 0 ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <EmptyState icon={KeyRound} title="Ingen passord ennå" description="Legg inn nettbank, Altinn, e-post, strømleverandør … og bruk «Gå til» for å hoppe rett inn." actionLabel="Legg til første passord" onAction={() => setSheet({ open: true, entry: null })} testId="vault-empty-state" />
          <Alert data-testid="vault-security-note">
            <ShieldCheck className="h-4 w-4" />
            <AlertTitle>Slik beskyttes hvelvet</AlertTitle>
            <AlertDescription>Innholdet krypteres med AES-256-GCM i nettleseren. Hvelvet låses automatisk etter {settings.auto_lock_minutes} min uten aktivitet. Du kan endre dette under Hvelvinnstillinger.</AlertDescription>
          </Alert>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Kategori" data-testid="vault-category-filter">
              {[["alle", `Alle (${entries.length})`], ...(favorites ? [["favoritter", `Favoritter (${favorites})`]] : []), ...usedCats.map((c) => [c, c])].map(([v, label]) => (
                <button key={v} type="button" role="tab" aria-selected={cat === v} onClick={() => setCat(v)} className={cn("rounded-full border px-3 py-1.5 text-sm transition-colors", cat === v ? "bg-accent text-accent-foreground border-transparent" : "hover:bg-secondary")} data-testid={`vault-cat-${v}`}>{v === "favoritter" ? <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5" /> {label}</span> : label}</button>
              ))}
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i navn, brukernavn, adresse …" className="pl-9 pr-9" aria-label="Søk i hvelvet" data-testid="vault-search-input" />
              {q ? <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Tøm søk" data-testid="vault-search-clear"><X className="h-4 w-4" /></button> : null}
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon={Search} title="Ingen treff" description="Prøv et annet søk eller en annen kategori." actionLabel="Nullstill" onAction={() => { setQ(""); setCat("alle"); }} testId="vault-no-results" />
          ) : (
            <ul className="space-y-2" data-testid="vault-entry-list">
              {filtered.map((e) => <EntryRow key={e.id} entry={e} settings={settings} leaked={breach.results?.[e.id] || 0} onEdit={(en) => setSheet({ open: true, entry: en })} onToggleFavorite={toggleFavorite} />)}
            </ul>
          )}
        </>
      )}

      <EntrySheet open={sheet.open} onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))} entry={sheet.entry} />
    </div>
  );
}
